import React, { useState, useEffect, useRef } from 'react';
import { 
  Key, 
  CheckCircle, 
  Activity,
  Shield, 
  UserPlus,
} from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import { db } from "../firebase";
import { 
  doc, 
  getDoc, 
  updateDoc, 
  increment, 
  arrayUnion, 
  Timestamp 
} from 'firebase/firestore';

export default function License() {
  const { isLicenseValid, setLicenseKey, mtAccount, setMtAccount } = useStore();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  
  const [licenseInput, setLicenseInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [justActivated, setJustActivated] = useState(false);
  const [isCheckingLicense, setIsCheckingLicense] = useState(true);

  // Check and validate license from Firebase on mount
  useEffect(() => {
    const checkLicenseFromFirebase = async () => {
      setIsCheckingLicense(true);
      
      try {
        // Get saved license key from localStorage
        const savedLicenseKey = localStorage.getItem('licenseKey');
        const savedAccount = localStorage.getItem('mtAccount');
        
        // Load account from localStorage if exists
        if (savedAccount) {
          try {
            const parsedAccount = JSON.parse(savedAccount);
            setMtAccount(parsedAccount);
          } catch (e) {
            console.error('Error parsing saved account:', e);
          }
        }
        
        // If no saved license key, nothing to check
        if (!savedLicenseKey) {
          setIsCheckingLicense(false);
          return;
        }
        
        // Check license in Firebase
        const licenseRef = doc(db, 'licenses', savedLicenseKey.toUpperCase());
        const licenseSnap = await getDoc(licenseRef);
        
        if (licenseSnap.exists()) {
          const data = licenseSnap.data();
          
          // Check expiration
          if (data.expiresAt) {
            const expiresDate = data.expiresAt.toDate();
            if (expiresDate < new Date()) {
              // License expired - clear it
              setLicenseKey(null, false);
              setIsCheckingLicense(false);
              return;
            }
          }
          
          // License is valid - set in state
          setLicenseKey(savedLicenseKey, true);
        } else {
          // License key not found in Firebase - clear it
          setLicenseKey(null, false);
        }
      } catch (err) {
        console.error('Error checking license:', err);
      } finally {
        setIsCheckingLicense(false);
      }
    };
    
    checkLicenseFromFirebase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check connection status and redirect after license validation
  useEffect(() => {
    if (isCheckingLicense) return;
    
    const timer = setTimeout(() => {
      const hasAccount = mtAccount && mtAccount.login;
      
      if (isLicenseValid && hasAccount) {
        // License valid and account connected → go to dashboard
        navigateRef.current('/dashboard', { replace: true });
      } else if (isLicenseValid && !hasAccount) {
        // License valid but no account connected → stay on license page (show connect button)
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [isLicenseValid, mtAccount, isCheckingLicense]);

  // Show loading while checking license from Firebase
  if (isCheckingLicense) {
    return (
      <div className="min-h-screen bg-theme flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-ocean-light border-t-transparent animate-spin"></div>
          <p className="text-theme-secondary">Verifying license...</p>
        </div>
      </div>
    );
  }

  const handleActivate = async () => {
    setError(null);
    if (!licenseInput.trim()) {
      setError('Please enter a license key');
      return;
    }

    const key = licenseInput.trim().toUpperCase();
    const currentLogin = mtAccount?.login;

    setLoading(true);

    try {
      const licenseRef = doc(db, 'licenses', key);
      const licenseSnap = await getDoc(licenseRef);

      if (!licenseSnap.exists()) {
        throw new Error('License key not found');
      }

      const data = licenseSnap.data();

      // Check expiration
      if (data.expiresAt) {
        const expiresDate = data.expiresAt.toDate();
        if (expiresDate < new Date()) {
          throw new Error(`License expired on ${expiresDate.toLocaleDateString()}`);
        }
      }

      // Check if already activated on this account (if account is connected)
      if (currentLogin) {
        const activatedLogins = data.activatedLogins || [];
        const alreadyUsedHere = activatedLogins.includes(currentLogin);

        if (alreadyUsedHere) {
          // Already activated on this account → just activate locally
          setLicenseKey(key, true);
          setJustActivated(true);
          // Auto-redirect to dashboard
          navigate('/dashboard');
          return;
        }

        // Check activation limit
        const currentActivations = data.activations || 0;
        const maxActivations = data.maxActivations || 1;

        if (currentActivations >= maxActivations) {
          throw new Error(`Activation limit reached (${maxActivations} account${maxActivations > 1 ? 's' : ''} allowed)`);
        }

        // Perform activation
        await updateDoc(licenseRef, {
          activations: increment(1),
          activatedLogins: arrayUnion(currentLogin),
          lastActivatedAt: Timestamp.now(),
        });
      }

      setLicenseKey(key, true);
      setJustActivated(true);
      
      // Auto-redirect based on account connection
      if (currentLogin) {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('License activation failed:', err);
      setError(err.message || 'Failed to activate license. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = () => {
    if (window.confirm('Are you sure you want to deactivate your license?')) {
      setLicenseKey(null, false);
      setLicenseInput('');
      setJustActivated(false);
      setError(null);
    }
  };

  // ────────────────────────────────────────────────
  // Active / Just activated UI
  // ────────────────────────────────────────────────
  if (isLicenseValid || justActivated) {
    return (
      <div className="min-h-screen bg-theme flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="card text-center">
            {justActivated && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-green-400 font-semibold">License activated successfully!</p>
              </div>
            )}

            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            
            <h2 className="text-2xl font-bold text-theme mb-2">License Active</h2>
            <p className="text-theme-secondary mb-6">
              {mtAccount?.login 
                ? 'Ready to trade with your connected MetaTrader account.'
                : 'License verified. Now connect your trading account.'
              }
            </p>

            {/* Show appropriate button based on account connection status */}
            {mtAccount?.login ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="btn-primary w-full mb-3 flex items-center justify-center gap-2"
              >
                Go to Dashboard
              </button>
            ) : (
              <button
                onClick={() => navigate('/connect')}
                className="btn-primary w-full mb-3 flex items-center justify-center gap-2"
              >
                <UserPlus className="w-5 h-5" />
                Connect Trading Account
              </button>
            )}

            <button
              onClick={handleDeactivate}
              className="w-full py-3 border-2 border-red-500/30 text-red-500 rounded-xl font-semibold hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2"
            >
              <Key className="w-5 h-5" />
              Deactivate License
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────
  // Activation form
  // ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-theme py-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-ocean-light to-teal-500 flex items-center justify-center shadow-ocean">
            <Activity className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-extrabold text-theme mb-2">TidalWave Pro</h1>
          <p className="text-theme-secondary">Intelligent Automated Trading</p>
        </div>

        {/* Form */}
        <div className="card">
          <h2 className="text-xl font-bold text-theme mb-2">Activate License</h2>
          <p className="text-theme-secondary text-sm mb-6">
            Enter your license key to activate TidalWave Pro.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-400/50 rounded-lg flex items-start gap-2 text-red-400 text-sm">
              <span>{error}</span>
            </div>
          )}

          {/* License Key Input */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-theme mb-2">
              License Key
            </label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-theme-muted" />
              <input
                type="text"
                className="input-field pl-11 uppercase"
                placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
                value={licenseInput}
                onChange={(e) => setLicenseInput(e.target.value.toUpperCase())}
                disabled={loading}
                maxLength={19}
              />
            </div>
          </div>

          {/* Activate Button */}
          <button
            onClick={handleActivate}
            disabled={loading || !licenseInput.trim()}
            className={`btn-primary w-full flex items-center justify-center gap-2
              ${(!licenseInput.trim()) ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <Shield className="w-5 h-5" />
            {loading ? 'Activating...' : 'Verify License Key'}
          </button>

          {/* Hint */}
          <p className="text-xs text-theme-muted text-center mt-4">
            Enter your license key to continue. Need a license? Contact support.
          </p>
        </div>
      </div>
    </div>
  );
}
